/**
 * Lab Flag Final Excel — generate + list BTS report jobs.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  cmApi,
  type TickerExcelGenerateRequest,
} from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { orderStakeBounds } from '@/features/bet-ticker/filters'
import type { TickerFilters } from '@/features/bet-ticker/domain'
import { marketNameForRequest } from '@/features/bet-ticker/marketType'

/** Map ticker filters → CM excel generate body (Lab generateExcelBts). */
export function buildExcelGenerateRequest(
  filters: TickerFilters,
): TickerExcelGenerateRequest {
  const marketName = marketNameForRequest(filters.market, filters.marketType)
  const { stakeMin, stakeMax } = orderStakeBounds(filters.stakeMin, filters.stakeMax)
  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    fromDate: filters.from,
    toDate: filters.to,
    ...(filters.tmId ? { tmId: filters.tmId } : {}),
    ...(filters.members.length
      ? { userName: filters.members }
      : filters.search
        ? { userName: filters.search }
        : {}),
    ...(filters.sport !== 'all' ? { eventType: filters.sport } : {}),
    ...(filters.event ? { eventName: filters.event } : {}),
    ...(marketName ? { marketName } : {}),
    ...(filters.marketType !== 'all' ? { marketType: filters.marketType } : {}),
    ...(filters.currency !== 'all' ? { currencyType: filters.currency } : {}),
    ...(stakeMin != null ? { stakeFrom: stakeMin } : {}),
    ...(stakeMax != null ? { stakeTo: stakeMax } : {}),
    ...(filters.flagged === 'flagged' ? { flaggedBets: true } : {}),
    ...(filters.flagged === 'final' ? { finallyFlaggedBets: true } : {}),
  }
}

export function useTickerExcelGenerate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: TickerExcelGenerateRequest) =>
      cmApi.tickerExcelGenerate(authHeaders(), body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cm', 'ticker-excel-list'] })
    },
  })
}

export function useTickerExcelList(page: number, enabled: boolean) {
  return useQuery({
    queryKey: ['cm', 'ticker-excel-list', page],
    queryFn: () => cmApi.tickerExcelList(authHeaders(), { page }),
    enabled,
    staleTime: 10_000,
  })
}
